/**
 * useWarning Hook
 *
 * Provides access to the warning system from any component.
 * Returns a safe fallback if used outside WarningProvider.
 */

import { useContext } from 'react';
import { WarningContext } from './WarningContext';
import type { WarningContextValue } from './types';

// Safe fallback when used outside provider
const noopContext: WarningContextValue = {
  warnings: [],
  showWarning: (warning) => {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[useWarning] No WarningProvider found. Warning not shown:', warning.message);
    }
  },
  dismissWarning: () => {},
  dismissAll: () => {},
};

/**
 * Hook to access the warning system.
 *
 * @returns WarningContextValue with methods to show/dismiss warnings
 *
 * @example
 * const { showWarning } = useWarning();
 *
 * showWarning({
 *   severity: 'warning',
 *   title: 'Validation Warning',
 *   message: 'Item has reached end of life',
 *   source: 'ERPNext',
 *   dismissable: true,
 * });
 */
export function useWarning(): WarningContextValue {
  const context = useContext(WarningContext);
  return context ?? noopContext;
}
